import type { ThemeProviderProps } from 'styled-components';
import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';
import type { Merge } from 'simplytyped';

const baseButtonStyles = ({
  theme,
  isLoading = false
}: Merge<
  ThemeProviderProps<any, any>,
  {
    isLoading?: boolean;
  }
>) => css`
  margin: ${theme.margins.default};
  font-weight: 600;
  font-size: ${theme.buttons.fontSize};
  color: var(--font-color);
  background-color: var(--background-color);
  border-radius: ${theme.buttons.borderRadius};
  cursor: pointer;
  text-align: center;
  position: relative;

  .material-symbols-rounded {
    font-size: ${theme.fonts.sizes.h3};
  }

  @keyframes spinner {
    to {
      transform: translate(50%, -50%) rotate(360deg);
    }
  }

  ${isLoading &&
  css`
    cursor: wait;
    pointer-events: none;

    * {
      visibility: hidden;
    }

    background-color: var(--background-color) !important;
    color: var(--background-color) !important;

    &::after {
      display: block;
      visibility: visible;
      color: var(--font-color) !important;
      border: 3px solid rgba(0, 0, 0, 0.1);
      border-left-color: inherit;
      border-radius: 50%;
      width: 10px;
      height: 10px;
      position: absolute;
      right: 50%;
      top: 50%;
      transform: translate(50%, -50%);
      content: '';
      animation: spinner 1s infinite linear;
    }
  `}

  &:disabled,
  &.disabled {
    color: ${theme.buttons.disabled.color};
    background-color: ${theme.buttons.disabled.background};
    border: ${theme.buttons.disabled.border};
    cursor: auto;

    @media (hover: hover) {
      &:hover {
        color: ${theme.buttons.disabled.color};
        background-color: ${theme.buttons.disabled.background};
      }
    }
  }

  .disabled {
    // Allows for disabling a button without disabling the click/hover event
    pointer-events: none;
  }
`;
const Button = styled.button<{ isLoading?: boolean }>(baseButtonStyles);
const NavLinkButton = styled(NavLink)(baseButtonStyles);

const PrimaryButton = styled(Button)(
  ({ theme }) => css`
    --font-color: ${theme.buttons.primary.color};
    --background-color: ${theme.buttons.primary.background};
    border: ${theme.buttons.primary.border};
    padding: ${theme.buttons.primary.padding};

    @media (hover: hover) {
      &:hover {
        color: ${theme.buttons.primary.colorHover};
        background-color: ${theme.buttons.primary.backgroundHover};
      }
    }
  `
);

const secondaryButtonStyles = ({ theme }: ThemeProviderProps<any, any>) => css`
  --font-color: ${theme.buttons.secondary.color};
  --background-color: ${theme.buttons.secondary.background};
  border: ${theme.buttons.secondary.border};
  padding: ${theme.buttons.secondary.padding};

  @media (hover: hover) {
    &:hover {
      color: ${theme.buttons.secondary.colorHover};
      background-color: ${theme.buttons.secondary.backgroundHover};
    }
  }
`;

const iconButtonStyles = ({ theme }: ThemeProviderProps<never, never>) => css`
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
`;

const SecondaryButton = styled(Button)(secondaryButtonStyles);
const SecondaryIconButton = styled(SecondaryButton)(iconButtonStyles);
const SecondaryNavLinkButton = styled(NavLinkButton)(secondaryButtonStyles);
const SecondaryTransparentButton = styled(SecondaryButton)(
  ({ theme }) => css`
    border-color: transparent !important;
    background-color: transparent !important;
  `
);

const PrimaryAltButton = styled(Button)(
  ({ theme }) => css`
    --font-color: ${theme.buttons.primaryAlt.color};
    --background-color: ${theme.buttons.primaryAlt.background};
    border: ${theme.buttons.primaryAlt.border};
    padding: ${theme.buttons.primaryAlt.padding};
    white-space: nowrap;

    @media (hover: hover) {
      &:hover {
        color: ${theme.buttons.primaryAlt.colorHover};
        background-color: ${theme.buttons.primaryAlt.backgroundHover};
      }
    }
  `
);

const PrimaryIconButton = styled(PrimaryButton)(iconButtonStyles);
const PrimaryAltIconButton = styled(PrimaryAltButton)(iconButtonStyles);

const SecondaryAltButton = styled(Button)(
  ({ theme }) => css`
    --font-color: ${theme.buttons.secondaryAlt.color};
    --background-color: ${theme.buttons.secondaryAlt.background};
    border: ${theme.buttons.secondaryAlt.border};
    padding: ${theme.buttons.secondaryAlt.padding};

    @media (hover: hover) {
      &:hover {
        color: ${theme.buttons.secondaryAlt.colorHover};
        background-color: ${theme.buttons.secondaryAlt.backgroundHover};
      }
    }
  `
);

const TransparentIconButton = styled(Button)<{ larger?: boolean }>(
  ({ theme, larger = false }) => css`
    --font-color: ${theme.colors.gray};
    --background-color: transparent;
    transition: opacity 0.3s ease;
    border: none;

    ${larger &&
    css`
      > span {
        font-size: 1.5rem !important;
      }
    `};

    @media (hover: hover) {
      &:not(:hover) {
        opacity: 0.4;
      }
    }
  `
);

type ActivateButtonProps = {
  deactivate?: boolean;
  onClick?: () => void;
};

const StyledActivateButton = styled(Button)<ActivateButtonProps>(
  ({ theme, deactivate }) => css`
    --font-color: ${theme.colors.white};
    --background-color: ${theme.colors.green};
    padding: ${theme.buttons.primary.padding};
    border: 0;

    @media (hover: hover) {
      &:hover {
        background-color: ${theme.colors.darkGreen};
      }
    }

    ${deactivate &&
    css`
      --background-color: ${theme.colors.main.error};

      @media (hover: hover) {
        &:hover {
          background-color: ${theme.colors.darkRed};
        }
      }
    `}

    .material-symbols-rounded {
      font-variation-settings: 'FILL' 0, 'wght' 700, 'GRAD' 0, 'opsz' 20 !important;
    }
  `
);

const ActivateButton = ({ deactivate, onClick }: ActivateButtonProps) => {
  return (
    <StyledActivateButton deactivate={deactivate} onClick={onClick}>
      {deactivate && (
        <>
          Deactivate <span className="material-symbols-rounded">cancel</span>
        </>
      )}
      {!deactivate && (
        <>
          Activate{' '}
          <span className="material-symbols-rounded">check_circle</span>
        </>
      )}
    </StyledActivateButton>
  );
};

type IconButtonProps = {
  iconType: 'Activate' | 'Deactivate' | 'Edit' | 'Apply' | 'Email';
  onClick?: () => void;
  size?: string;
};

const StyledIconButton = styled.button<IconButtonProps>(
  ({ theme, iconType, size }) => css`
    color: ${theme.colors.main.accent};
    padding: 0 0.25rem;
    border: 0;
    background-color: transparent;
    opacity: 0.75;
    transition: opacity 0.35s ease !important;

    .material-symbols-rounded {
      font-size: ${size || '20px'};
    }

    @media (hover: hover) {
      &:hover {
        opacity: 1;
        cursor: pointer;
      }
    }

    ${iconType === 'Activate' &&
    css`
      color: ${theme.colors.green};
    `}

    ${iconType === 'Deactivate' &&
    css`
      color: ${theme.colors.main.error};
    `}

    ${iconType === 'Apply' &&
    css`
      color: ${theme.colors.main.secondary};
    `}
  `
);

const IconButton = ({ iconType, onClick, size }: IconButtonProps) => {
  return (
    <StyledIconButton iconType={iconType} onClick={onClick} size={size}>
      {iconType === 'Activate' && (
        <span className="material-symbols-rounded no-fill">check_circle</span>
      )}
      {iconType === 'Deactivate' && (
        <span className="material-symbols-rounded no-fill">cancel</span>
      )}
      {iconType === 'Edit' && (
        <span className="material-symbols-rounded">edit</span>
      )}
      {iconType === 'Apply' && (
        <span className="material-symbols-rounded no-fill">export_notes</span>
      )}
      {iconType === 'Email' && (
        <span className="material-symbols-rounded no-fill">
          forward_to_inbox
        </span>
      )}
    </StyledIconButton>
  );
};

type ToggleButtonProps = {
  children: React.ReactNode;
  alternative?: boolean;
  onClick?: () => void;
};

const ArrowIcon = styled.span`
  position: relative;
  margin-left: 5px;
  transition: 0.35s ease;
`;

const StyledToggleButton = styled(Button)<ToggleButtonProps>(
  ({ theme, alternative }) => css`
    --font-color: ${theme.buttons.secondary.color};
    --background-color: ${theme.buttons.secondary.background};
    border: ${theme.buttons.secondary.border};
    padding: ${theme.buttons.secondary.padding};

    @media (hover: hover) {
      &:hover {
        color: ${theme.buttons.secondary.colorHover};
        background-color: ${theme.buttons.secondary.backgroundHover};
      }
    }

    ${alternative &&
    css`
      --font-color: ${theme.buttons.primary.color};
      --background-color: ${theme.buttons.primary.background};
      border: ${theme.buttons.primary.border};
      padding: ${theme.buttons.primary.padding};

      ${ArrowIcon} {
        transform: rotate(-180deg);
      }

      @media (hover: hover) {
        &:hover {
          color: ${theme.buttons.primary.colorHover};
          background-color: ${theme.buttons.primary.backgroundHover};
        }
      }
    `}
  `
);

const ToggleButton = ({
  children,
  alternative,
  onClick
}: ToggleButtonProps) => {
  return (
    <StyledToggleButton alternative={alternative} onClick={onClick}>
      {children}
      <ArrowIcon className="material-symbols-rounded">expand_more</ArrowIcon>
    </StyledToggleButton>
  );
};

type ButtonContainerProps = {
  horizontalSpacing: string;
  verticalSpacing: string;
  justify?: string;
};

const ButtonContainer = styled.div<ButtonContainerProps>(
  ({ horizontalSpacing, verticalSpacing, justify }) => css`
    display: flex;
    flex-wrap: wrap;
    margin-left: -${horizontalSpacing};
    margin-right: -${horizontalSpacing};
    justify-content: ${justify};

    button,
    a {
      margin: 0px ${horizontalSpacing} ${verticalSpacing};
    }
  `
);

const RoundedButton = styled(Button)(
  ({ theme }) => css`
    border-radius: 16px;
  `
);

export {
  RoundedButton,
  PrimaryButton,
  SecondaryButton,
  PrimaryAltButton,
  SecondaryAltButton,
  ActivateButton,
  ToggleButton,
  ButtonContainer,
  PrimaryIconButton,
  PrimaryAltIconButton,
  SecondaryIconButton,
  SecondaryNavLinkButton,
  SecondaryTransparentButton,
  TransparentIconButton,
  IconButton
};
